import {
  IPropertyMapping,
  ISelectedMappingNoQuery,
  MappingPropConceptInfoType,
} from './properties.interface';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  emptySelectedProperty,
  mappingDefBase,
  mappingFunctionDef,
  mappingInitialState,
  relationLookupsDefaults,
} from './initialState';
import { IConceptIDOverlaps, IDataSet, IMappingState } from './mapping.interface';
import { getLookupDefaultValuesForNewMappings } from './helpers';
import { updateConceptIdOverlapsSlice } from './mapping-conceptIdOverlaps-redux-slice';

export const updateMappingReduxSlice = {
  resetState: (state: IMappingState) => {
    state.selectedProperty = mappingInitialState.selectedProperty;
    state.selectedMapping = mappingInitialState.selectedMapping;
    state.dataSets = mappingInitialState.dataSets;
    state.lookups = mappingInitialState.lookups;
    state.conceptIdOverlaps = mappingInitialState.conceptIdOverlaps;
    state.loading = mappingInitialState.loading;
    state.showMappingModal = false;
  },

  setCanEdit: (state: IMappingState, action: PayloadAction<boolean>) => {
    state.canEdit = action.payload;
  },

  openMappingModal: (state: IMappingState) => {
    state.showMappingModal = true;
  },

  setIsLoadingMapping: (state: IMappingState, action: PayloadAction<boolean>) => {
    state.loading = action.payload;
    state.showMappingModal = true;
  },

  setSelectedProperty: (
    state: IMappingState,
    action: PayloadAction<{
      selectedProperty: IPropertyMapping;
      selectedConcept: MappingPropConceptInfoType;
    }>
  ) => {
    state.selectedProperty = action.payload?.selectedProperty || emptySelectedProperty;
    // @ts-ignore
    state.selectedMapping = {
      ...mappingDefBase,
      ...state.selectedMapping,
      conceptUri: action.payload.selectedConcept.conceptUri,
      propertyUri: action.payload.selectedProperty.uri,
      // NOTE: when fetching props, key name is propTypeUri BUT when saving a def, BE requires 'propertyTypeUri'
      propertyTypeUri: action.payload.selectedProperty.propTypeUri,

      // used for displaying prop name & concept
      modalLabels: {
        propName: action.payload.selectedProperty.label,
        conceptName: action.payload.selectedConcept.label,
      },
    };
  },

  initFetchingDataSetColumns: (state: IMappingState) => {
    state.dataSets.dataSetColumns.list = [];
    state.dataSets.dataSetColumns.errors = [];
    state.dataSets.dataSetColumns.options = [];
    state.dataSets.dataSetColumns.loading = true;
    state.dataSets.dataSetColumns.hasError = false;
  },

  setHasErrorLoadingDataSetColumns: (state: IMappingState, action: PayloadAction<boolean>) => {
    state.dataSets.dataSetColumns.hasError = action.payload;
    state.dataSets.dataSetColumns.loading = false;
  },

  setInitialSelectedMappingInfo: (
    state: IMappingState,
    action: PayloadAction<{
      mappingDefinition: ISelectedMappingNoQuery;
      dataSetMap?: Record<number, IDataSet>;
      datasetId?: number;
    }>
  ) => {
    const { mappingDefinition: mappingDef, dataSetMap, datasetId } = action.payload;
    const selectedDataSet = datasetId && dataSetMap ? dataSetMap[datasetId] : null;

    state.selectedMapping = {
      // Set mappingDefBase as defaults
      ...mappingDefBase,

      // Merge in mappingDef from the backend
      ...mappingDef,

      // Convert to 0 if it used to be 1 (old logic)
      mappingIdTypeId: mappingDef.mappingIdTypeId === 1 ? 0 : mappingDef.mappingIdTypeId,

      // Keep existing properties used for labeling
      modalLabels: state.selectedMapping?.modalLabels || {
        propName: '',
        conceptName: '',
      },

      // Override defaults from dataSets
      timestampProps: {
        // Set the timestampFormat to the mappingDef setting, OR the dataset's timestampFormat, OR null if not set
        timestampFormat:
          mappingDef?.timestampProps?.timestampFormat ??
          selectedDataSet?.dataSetProps?.timeFormat ??
          null,

        // Set the timezone to the mappingDef setting, OR the dataset's timezone, OR null if not set
        timeZone:
          mappingDef?.timestampProps?.timeZone ?? selectedDataSet?.dataSetProps?.timezone ?? null,
        alignTo: mappingDef.timestampProps?.alignTo,
      },
    };

    // lookups
    state.lookups.relations.overlapChecked = !!mappingDef?.relationLookups?.datasourceId;
    state.lookups.identifier.overlapChecked =
      mappingDef.overlapDatasourceId !== null && mappingDef.overlapDatasourceId !== undefined;
  },

  setMappingInfoLocal: (
    state: IMappingState,
    action: PayloadAction<Partial<ISelectedMappingNoQuery>>
  ) => {
    (Object.keys(action.payload) as Array<keyof ISelectedMappingNoQuery>).forEach((key) => {
      const typedKey = key as keyof ISelectedMappingNoQuery;
      const value = action.payload[key];

      if (value !== undefined && state.selectedMapping !== null) {
        (state.selectedMapping[typedKey] as typeof value) = value;
      }
    });
  },

  setDatasetColumnsForMapping: (state: IMappingState, action: PayloadAction<any>) => {
    // TODO: remove sorting once it comes back sorted from BE
    const dataSetColumns = [...action.payload].sort((a: string, b: string) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    state.dataSets.dataSetColumns.loading = false;
    state.dataSets.dataSetColumns.list = dataSetColumns;
    state.dataSets.dataSetColumns.options = dataSetColumns.map((column: string) => ({
      label: column,
      value: column,
    }));
  },
};

export const updateRelationLookupsSlice = {
  setLookupsIsLoading: (state: IMappingState, action: PayloadAction<boolean>) => {
    state.lookups.loading = action.payload;
    state.lookups.error = null;
  },
  setGeneralLookupsDataSetColumns: (state: IMappingState, action: PayloadAction<any>) => {
    state.lookups.loading = false;
    state.lookups.relations.dataSetColumns.list = action.payload;
    state.lookups.relations.dataSetColumns.options = action.payload.map((column: string) => ({
      label: column,
      value: column,
    }));
  },
  setConceptIdLookupsInitialState: (state: IMappingState, action: PayloadAction<any>) => {
    const { lookups, overlaps } = action.payload;

    // set overlap defaults for new mappings
    if (state.selectedMapping?.id === null) {
      const { overlapDefaultValues, overlapChecked } =
        getLookupDefaultValuesForNewMappings(lookups);
      state.selectedMapping = {
        ...state.selectedMapping,
        ...overlapDefaultValues,
      };
      state.lookups.identifier.overlapChecked = overlapChecked;
    }

    let overlapSelectedDataset = null;
    if (lookups.length > 0) {
      overlapSelectedDataset = overlaps.find(
        (item: IDataSet) => item.dataSourceId === state.selectedMapping?.overlapDatasourceId
      );
    }
    state.lookups.identifier.overlapSelectedDataset = overlapSelectedDataset || null;
    state.lookups.identifier.overlapSelectedDatasetOptions = (
      overlapSelectedDataset?.columnNames || []
    ).map((item: string) => ({ value: item, label: item }));
    state.lookups.identifier.overlapChecked = overlapSelectedDataset ? lookups.length > 0 : false;
  },
  setLookupsHasError: (state: IMappingState) => {
    state.lookups.loading = false;
    state.lookups.error.type = 'loading';
    state.lookups.error.message = 'Error fetching mapping data';
  },
  toggleGeneralLookupsState: (state: IMappingState, action: PayloadAction<boolean>) => {
    if (action.payload) {
      state.lookups.relations.overlapChecked = true;
    } else {
      state.lookups.relations = mappingInitialState.lookups.relations;
      state.selectedMapping!.relationLookups = relationLookupsDefaults;
    }
  },
  // conceptId lookups
  toggleConceptIdLookupsState: (state: IMappingState, action: PayloadAction<boolean>) => {
    if (action.payload) {
      state.lookups.identifier.overlapChecked = true;
    } else {
      state.lookups.identifier = mappingInitialState.lookups.identifier;
      state.selectedMapping!.mappingOverlapIdTypeId = 0; // Resets to default which is "By identifier column"
      state.selectedMapping!.overlapIdFunction = mappingFunctionDef;
      state.selectedMapping!.overlapTableName = null;
      state.selectedMapping!.overlapDatasourceId = null;
      state.selectedMapping!.overlapColumnName = null;
    }
  },
  updateGeneralOverlapDatasetForRelations: (
    state: IMappingState,
    action: PayloadAction<IConceptIDOverlaps | null>
  ) => {
    state.lookups.relations.overlapSelectedDataset = action.payload;
  },

  updateConceptIdOverlapDataset: (
    state: IMappingState,
    action: PayloadAction<IConceptIDOverlaps | null>
  ) => {
    state.lookups.identifier.overlapSelectedDataset = action.payload;

    state.lookups.identifier.overlapSelectedDatasetOptions = (
      action?.payload?.columnNames || []
    ).map((item) => ({ label: item, value: item }));
  },

  initFetchingTimeSeriesDataSetColumns: (state: IMappingState) => {
    state.dataSets.timeSeriesDataSetColumns.list = [];
    state.dataSets.timeSeriesDataSetColumns.errors = [];
    state.dataSets.timeSeriesDataSetColumns.options = [];
    state.dataSets.timeSeriesDataSetColumns.loading = true;
    state.dataSets.timeSeriesDataSetColumns.hasError = false;
  },

  setHasErrorLoadingTimeSeriesDataSetColumns: (
    state: IMappingState,
    action: PayloadAction<boolean>
  ) => {
    state.dataSets.timeSeriesDataSetColumns.hasError = action.payload;
    state.dataSets.timeSeriesDataSetColumns.loading = false;
  },

  setDataSetColumnsForTimeSeriesMapping: (state: IMappingState, action: PayloadAction<any>) => {
    const dataSetColumns = [...action.payload].sort((a: string, b: string) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    state.dataSets.timeSeriesDataSetColumns.loading = false;
    state.dataSets.timeSeriesDataSetColumns.list = dataSetColumns;
    state.dataSets.timeSeriesDataSetColumns.options = dataSetColumns.map((column: string) => ({
      label: column,
      value: column,
    }));
  },
};

const MappingReduxSlice = createSlice({
  name: 'mapping',
  initialState: mappingInitialState,
  reducers: {
    ...updateMappingReduxSlice,
    ...updateRelationLookupsSlice,
    ...updateConceptIdOverlapsSlice,
  },
});

export const {
  resetState,
  setCanEdit,
  openMappingModal,
  setIsLoadingMapping,
  setSelectedProperty,
  setInitialSelectedMappingInfo,
  setDatasetColumnsForMapping,
  setMappingInfoLocal,
  toggleGeneralLookupsState,
  setLookupsIsLoading,
  setLookupsHasError,
  toggleConceptIdLookupsState,
  updateGeneralOverlapDatasetForRelations,
  updateConceptIdOverlapDataset,
  setGeneralLookupsDataSetColumns,
  setHasErrorLoadingDataSetColumns,
  initFetchingDataSetColumns,
  setConceptIdOverlaps,
  setConceptIdLookupsInitialState,
  initFetchingTimeSeriesDataSetColumns,
  setHasErrorLoadingTimeSeriesDataSetColumns,
  setDataSetColumnsForTimeSeriesMapping,
} = MappingReduxSlice.actions;

export default MappingReduxSlice.reducer;
